import { createClient, createAuthRefresh } from "@workspace/network";
import type {
  GenerationMetadataRecord,
  GpuHealth,
  Job,
  JobStatus,
  PipelineManifest,
  UploadResponse,
} from "./types";

const API_BASE =
  process.env.NEXT_PUBLIC_3DGEN_API || "https://api.3dgen.l.gaz.codes";

const client = createClient({
  baseUrl: API_BASE,
  credentials: "include",
  timeout: 30_000,
  retry: {},
  auth: { onUnauthorized: createAuthRefresh() },
});

export function apiBase(): string {
  return API_BASE;
}

// ─── Pipelines / health ─────────────────────────────────────────────────────

export async function listPipelines(): Promise<PipelineManifest[]> {
  const { data } = await client.get<PipelineManifest[]>("/v1/pipelines");
  return data;
}

export async function getHealth(): Promise<GpuHealth> {
  const { data } = await client.get<GpuHealth>("/v1/health");
  return data;
}

export async function warmup(models: string[]): Promise<{
  alreadyLoaded: string[];
  loading: string[];
  unavailable: string[];
}> {
  const { data } = await client.post<{
    alreadyLoaded: string[];
    loading: string[];
    unavailable: string[];
  }>("/v1/warmup", { models });
  return data;
}

// ─── Uploads ────────────────────────────────────────────────────────────────

export async function uploadImage(file: File): Promise<UploadResponse> {
  const buf = await file.arrayBuffer();
  const { data } = await client.post<UploadResponse>("/v1/uploads", buf, {
    rawBody: true,
    headers: { "Content-Type": file.type || "image/png" },
    timeout: 60_000,
  });
  return data;
}

// ─── Jobs ───────────────────────────────────────────────────────────────────

export interface SubmitJobParams {
  pipelineId: string;
  imageKeys: string[];
  options?: Record<string, unknown>;
  label?: string;
}

export async function submitJob(params: SubmitJobParams): Promise<Job> {
  const { data } = await client.post<Job>("/v1/jobs", {
    pipelineId: params.pipelineId,
    imageKeys: params.imageKeys,
    options: params.options ?? {},
    label: params.label,
  });
  return data;
}

export async function listJobs(opts: {
  limit?: number;
  cursor?: string;
  status?: JobStatus;
} = {}): Promise<{ jobs: Job[]; cursor?: string }> {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.cursor) params.set("cursor", opts.cursor);
  if (opts.status) params.set("status", opts.status);
  const qs = params.toString();
  const { data } = await client.get<{ jobs: Job[]; cursor?: string }>(
    `/v1/jobs${qs ? `?${qs}` : ""}`,
  );
  return data;
}

export async function getJob(id: string): Promise<Job> {
  const { data } = await client.get<Job>(`/v1/jobs/${encodeURIComponent(id)}`);
  return data;
}

export async function cancelJob(id: string): Promise<Job> {
  const { data } = await client.post<Job>(
    `/v1/jobs/${encodeURIComponent(id)}/cancel`,
  );
  return data;
}

export async function retryJob(id: string): Promise<Job> {
  const { data } = await client.post<Job>(
    `/v1/jobs/${encodeURIComponent(id)}/retry`,
  );
  return data;
}

export async function deleteJob(id: string): Promise<void> {
  await client.delete(`/v1/jobs/${encodeURIComponent(id)}`);
}

// ─── Generations (history, list also surfaces pre-loss SF3D records) ───────

export async function listGenerations(opts: {
  limit?: number;
  cursor?: string;
} = {}): Promise<{ items: GenerationMetadataRecord[]; cursor?: string }> {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.cursor) params.set("cursor", opts.cursor);
  const qs = params.toString();
  const { data } = await client.get<{
    items: GenerationMetadataRecord[];
    cursor?: string;
  }>(`/v1/generations${qs ? `?${qs}` : ""}`);
  return data;
}

/**
 * Authenticated fetch of a GLB. <model-viewer> can't send cookies via the
 * `src` attribute, so the FE pulls bytes here and hands a blob URL to it.
 */
export async function fetchGlbBlobUrl(jobId: string): Promise<string> {
  const res = await fetch(
    `${API_BASE}/v1/generations/${encodeURIComponent(jobId)}/glb`,
    { credentials: "include" },
  );
  if (!res.ok) {
    throw new Error(`GLB fetch failed: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ─── SSE helpers ────────────────────────────────────────────────────────────

export function healthEventSource(): EventSource {
  return new EventSource(`${API_BASE}/v1/health/stream`, {
    withCredentials: true,
  });
}

export function jobEventSource(jobId: string): EventSource {
  return new EventSource(
    `${API_BASE}/v1/jobs/${encodeURIComponent(jobId)}/stream`,
    { withCredentials: true },
  );
}

export function userJobsEventSource(): EventSource {
  return new EventSource(`${API_BASE}/v1/jobs/stream`, {
    withCredentials: true,
  });
}
